import React from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from "react-native";
import { ArrowLeftIcon } from "@phosphor-icons/react";

import {
  CourseDetailedAnalytics,
  type GetCourseCompletionDetailsQuery,
  ModuleAnalytics,
} from "@axio/graphql/generated/graphql";
import { getSentimentColor } from "../../../../utils/analyticsUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import ModulePanel from "../components/ModulePanel";

const hasEngagement = (item: ModuleAnalytics): boolean => {
  return (
    (item.success_points && item.success_points > 0) ||
    (item.confusion_points && item.confusion_points > 0) ||
    (item.questions_asked && item.questions_asked > 0) ||
    (item.active_interaction_count && item.active_interaction_count > 0) ||
    (item.distractions && item.distractions > 0) ||
    (item.average_time_taken && item.average_time_taken > 0)
  );
};

const sortByEngagement = (items: ModuleAnalytics[]): ModuleAnalytics[] => {
  return [...items].sort((a, b) => {
    const aActive = hasEngagement(a);
    const bActive = hasEngagement(b);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
};

type CourseCompletionDetail = GetCourseCompletionDetailsQuery["getCourseCompletionDetails"]["data"][number];

interface CourseDetailProps {
  courseId: string;
  onBackToCourses: () => void;
  completionData: CourseCompletionDetail[];
  detailedCourse: CourseDetailedAnalytics | null;
  loading: boolean;
}

const CourseDetail: React.FC<CourseDetailProps> = ({
  courseId,
  onBackToCourses,
  completionData,
  detailedCourse,
  loading: externalLoading,
}) => {
  const completion = completionData.find((c) => c.id === courseId);

  if (externalLoading) {
    return (
      <View style={styles.loadingcontainer}>
        <ActivityIndicator />
        <CustomText text="Loading course details..." />
      </View>
    );
  }

  const renderModules = (modules: ModuleAnalytics[]) => {
    if (!modules || modules.length === 0) {
      return <CustomText text="No modules found." size="s" />;
    }
    const sorted = sortByEngagement(modules);
    return (
      <View>
        {sorted.map((module, index) => (
          <ModulePanel
            key={module.id || index}
            module={module}
            index={index}
            inactive={!hasEngagement(module)}
            loading={externalLoading}
          />
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} tabIndex={0}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToCourses}>
        <ArrowLeftIcon size={20} color="#2D2F36" />
        <CustomText text="Back to Courses" />
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="s" />

      <View style={styles.header}>
        <CustomText text={detailedCourse?.label} size="xl" weight="bold" useTranslationText={false} />
        {detailedCourse?.sentiment && (
          <View
            style={[
              styles.sentimentBadge,
              {
                backgroundColor: getSentimentColor(detailedCourse?.sentiment) + "20",
                borderColor: getSentimentColor(detailedCourse?.sentiment),
              },
            ]}>
            <CustomText
              text={detailedCourse?.sentiment}
              size="s"
              style={{ color: getSentimentColor(detailedCourse?.sentiment), textTransform: "capitalize" }}
              useTranslationText={false}
            />
          </View>
        )}
      </View>

      <CustomSpacing type="vertical" size="s" />

      {/* Key Metrics */}
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <CustomText text="Success Points" size="s" weight="bold" style={{ color: "#10B981" }} />
          <CustomText text={`${detailedCourse?.success_points}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText text="Confusion Points" size="s" weight="bold" style={{ color: "#EF4444" }} />
          <CustomText text={`${detailedCourse?.confusion_points}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText text="Questions Asked" size="s" weight="bold" style={{ color: "#3B82F6" }} />
          <CustomText text={`${detailedCourse?.questions_asked}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText text="Enrollment" size="s" weight="bold" style={{ color: "#8B5CF6" }} />
          <CustomText text={`${detailedCourse?.enrollment}`} size="xl" weight="bold" useTranslationText={false} />
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Completion Statistics */}
      {completion && (
        <View style={styles.completionCard}>
          <CustomText text="Completion Statistics" size="l" weight="bold" />

          <CustomSpacing type="vertical" size="s" />

          <View style={styles.completionRow}>
            <CustomText text="completed_label" />
            <CustomText text={`${completion.completionStats.completed}`} weight="bold" useTranslationText={false} />
          </View>
          <View style={styles.completionRow}>
            <CustomText text="in_progress_label" />
            <CustomText text={`${completion.completionStats.inProgress}`} weight="bold" useTranslationText={false} />
          </View>
          <View style={styles.completionRow}>
            <CustomText text="not_started_label" />
            <CustomText text={`${completion.completionStats.notStarted}`} weight="bold" useTranslationText={false} />
          </View>
        </View>
      )}

      <CustomSpacing type="vertical" size="l" />

      {/* Course Modules Drill-down */}
      <View style={styles.modulesSection}>
        <CustomText text="Course Modules" size="l" weight="bold" />

        <CustomSpacing type="vertical" size="s" />

        {detailedCourse?.details?.modules ? (
          renderModules(detailedCourse?.details.modules)
        ) : (
          <CustomText text="No module data available." size="s" />
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  loadingcontainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    padding: 16,
  },
  container: {
    flex: 1,
    // backgroundColor: "#FFFFFF",
    padding: 16,
  },
  backButton: {
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    alignSelf: "flex-start",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  sentimentBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    marginLeft: 8,
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  statCard: {
    flex: 1,
    minWidth: 120,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  completionCard: {
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  completionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  modulesSection: {
    marginBottom: 24,
  },
});

export default CourseDetail;
